"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as CircleHelp, ShieldCheck, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  category: "Power & Setup",
  question: "What electrical power is required to run a refrigerated trailer?",
  answer: "The 6×8, 7×12, and 7×16 trailers operate using one standard 115-volt, 15-amp electrical circuit. A dedicated circuit is recommended, and an optional Honda 3000 generator may be available when suitable site power is unavailable. Power and fuel requirements for the 53-foot reefer trailer are confirmed individually."
}, {
  id: "item-2",
  category: "Delivery",
  question: "How is the trailer delivered and set up on site?",
  answer: "Delivery, positioning, setup, and pickup arrangements are scheduled and confirmed with each quote. Requirements vary based on trailer size, delivery location, and site access. Delivery and site-access requirements for the 53-foot reefer trailer are reviewed individually."
}, {
  id: "item-3",
  category: "Capabilities",
  question: "Can the trailers operate as both coolers and freezers?",
  answer: "The 6×8, 7×12, and 7×16 trailers are configured for refrigerated and freezer applications when properly connected and operated. Temperature requirements and intended use must be confirmed with Fridge Fleet. The 53-foot reefer trailer's temperature range and intended use are confirmed individually."
}, {
  id: "item-4",
  category: "Cold Storage",
  question: "What can a mobile cold storage trailer be used for?",
  answer: "Potential applications include food and beverage storage, catering, events, farmers markets, food service, commercial overflow, grocery operations, food distribution, and temporary commercial cold storage. Trailer size, availability, and suitability for each application are confirmed individually."
}, {
  id: "item-5",
  category: "Scheduling",
  question: "How far in advance should I reserve a trailer?",
  answer: "Request availability as early as possible and provide your rental dates, delivery location, intended use, and preferred trailer size. Availability, rental terms, and delivery and pickup scheduling are confirmed with each quote."
}, {
  id: "item-6",
  category: "Coverage",
  question: "What areas do you deliver to?",
  answer: "Fridge Fleet serves Southern California and the listed Las Vegas-area communities. Coverage, trailer availability, delivery requirements, site access, and scheduling depend on the location, rental dates, and trailer size and are confirmed individually."
}, {
  id: "item-7",
  category: "53-Foot Reefer",
  question: "What should I know about the 53-foot reefer trailer?",
  answer: "The 53-foot reefer trailer may provide large-capacity temporary refrigerated storage for commercial needs. Typical exterior dimensions are 53′ L × 102.36″ W × 13′ 6″ H, and typical interior dimensions are approximately 52′ 2″ L × 97.4″ W × 105.5″ H. These are typical manufacturer dimensions and may vary by configuration. Exact availability, specifications, temperature range, power or fuel requirements, capacity, loading requirements, delivery requirements, site access, and rental duration are confirmed individually."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="faq-accordion" data-nav="light" className="relative bg-background py-16 md:py-24 lg:py-28 border-b border-border text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={fadeInVariants} className="mx-auto max-w-3xl text-center space-y-4 mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs md:text-sm font-semibold tracking-wide uppercase">
            <span className="size-2 rounded-full bg-primary" />
            COMMONLY ASKED QUESTIONS
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            Frequently Asked Questions
          </h1>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Find general information about Fridge Fleet&apos;s 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus the 53-foot reefer trailer. Availability, specifications, delivery requirements, and suitability are confirmed with each quote.
          </p>
        </motion.div>

        {/* Main Content Grid: Accordion Left, Contact Card Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-30px"
        }} variants={fadeInVariants} className="lg:col-span-8 bg-card border border-border rounded-lg shadow-sm p-4 sm:p-6 md:p-8">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
              {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="border border-border/80 rounded-md px-4 py-1 transition-colors hover:border-primary/40 bg-background/50 data-[state=open]:bg-background data-[state=open]:border-primary/50">
                    <AccordionTrigger className="text-left font-default font-semibold text-base sm:text-lg text-foreground hover:no-underline py-4">
                      <span className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1 pr-2 sm:pr-4">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground uppercase shrink-0">
                          {faq.category}
                        </span>
                        <span className="min-w-0 break-words">{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4 pr-6">
                      {faq.answer}
                      {faq.id === "item-7" && <div className="mt-3">
                          <Link to="/FootRefrigeratedTrailerRentalsPage" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline">
                            View the 53-foot reefer trailer
                            <ArrowRight className="size-4" />
                          </Link>
                        </div>}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Quick Support & Dispatch Card Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-30px"
        }} variants={fadeInVariants} className="lg:col-span-4 space-y-6">
            {/* Direct Dispatch Card */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-md bg-accent flex items-center justify-center text-accent-foreground shrink-0">
                  <CircleHelp className="size-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground font-default">
                    Need Help Choosing a Trailer?
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Tell us your rental dates, delivery location, intended use, temperature requirements, site-access details, and available power so we can help identify an appropriate trailer option.
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed">
                Have specific requirements? Request a quote and our team will follow up.
              </p>

              <div className="space-y-3 pt-2">
                <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 shadow-xs">
                  <Link to="/ContactRequestAQuotePage">
                    Request a Quote
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>

                <a href="tel:2135218821" className="w-full flex items-center justify-center gap-2 rounded-md bg-muted text-muted-foreground font-semibold text-sm h-11 px-4 border border-border">
                  213-521-8821
                </a>
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-4 text-accent-foreground shrink-0" />
                  <span>Trailer options include 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability and requirements are confirmed individually.</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Zap className="size-4 text-accent-foreground shrink-0" />
                  <span>Serving Southern California and our listed Las Vegas-area communities.</span>
                </div>
              </div>
            </div>

            {/* Quick Link Card */}
            <div className="bg-muted/60 border border-border rounded-lg p-5 space-y-3">
              <h3 className="text-sm font-semibold text-foreground font-default">
                Explore Trailer Options
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Review verified specifications for the 6×8, 7×12, and 7×16 trailers, plus typical dimensions and pending details for the 53-foot reefer trailer.
              </p>
              <Button asChild variant="outline" size="sm" className="w-full text-xs font-semibold">
                <Link to="/RefrigeratedTrailersPage">
                  View Trailer Lineup
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}